import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Check, ArrowRight, Server, Shield, Cloud, Database, Network, Lock, Sparkles, HeadphonesIcon, Scale, X } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ServiceDetailDialog from '@/components/ServiceDetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { Server, Shield, Cloud, Database, Network, Lock, Sparkles, HeadphonesIcon, Headphones: HeadphonesIcon };

const fmtUSD = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

/**
 * Public /pricing page — comparison-style overview driven by the live services
 * catalog. Three sections:
 *   - Tiered services rendered as a side-by-side tier-comparison table per service
 *   - Per-device services rendered as base + per-device pricing rows
 *   - Flat-rate services rendered as compact monthly cards
 *
 * "Get started" / "Request quote" CTA on every row opens the existing
 * ServiceDetailDialog in 'quote' mode so the visitor can submit contact info
 * and create a draft Quote in the admin queue.
 */
const Pricing = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeService, setActiveService] = useState(null);
  // Compare drawer state — set of service_ids the visitor has checked. Capped at 3
  // to keep the side-by-side comparison readable on most screens.
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const MAX_COMPARE = 3;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/services`);
        setServices(res.data || []);
      } catch (e) {
        // Non-fatal — empty page renders a friendly fallback.
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const grouped = useMemo(() => {
    const tiered = [];
    const perDevice = [];
    const flat = [];
    for (const s of services) {
      const pt = s.pricing_type || 'flat';
      if (pt === 'tiered' && Array.isArray(s.tiers) && s.tiers.length > 0) tiered.push(s);
      else if (pt === 'per_device') perDevice.push(s);
      else flat.push(s);
    }
    return { tiered, perDevice, flat };
  }, [services]);

  const openDialog = (service) => setActiveService(service);

  const toggleCompare = (serviceId) => {
    setCompareIds((prev) => {
      if (prev.includes(serviceId)) return prev.filter((id) => id !== serviceId);
      if (prev.length >= MAX_COMPARE) return prev; // soft-cap, ignored
      return [...prev, serviceId];
    });
  };

  const compareServices = useMemo(
    () => compareIds.map((id) => services.find((s) => s.service_id === id)).filter(Boolean),
    [compareIds, services],
  );

  // Drag-and-drop reorder for the compare drawer columns. Tracks the index
  // currently being dragged + the index hovered over so we can reorder live.
  const [dragFromIdx, setDragFromIdx] = useState(null);

  const handleDragStart = (idx) => () => setDragFromIdx(idx);
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragFromIdx === null || dragFromIdx === idx) return;
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragFromIdx === null || dragFromIdx === idx) {
      setDragFromIdx(null);
      return;
    }
    setCompareIds((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragFromIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragFromIdx(null);
  };
  const handleDragEnd = () => setDragFromIdx(null);

  // Pricing summary used by the compare drawer.
  const summarisePricing = (s) => {
    if (!s) return { startingAt: 0, label: 'Flat' };
    const pt = s.pricing_type || 'flat';
    if (pt === 'tiered' && s.tiers?.length) {
      const lo = Math.min(...s.tiers.map((t) => Number(t.price) || 0));
      return { startingAt: lo, label: `${s.tiers.length} tier${s.tiers.length === 1 ? '' : 's'}` };
    }
    if (pt === 'per_device') {
      return { startingAt: (Number(s.price) || 0) + (Number(s.per_device_price) || 0), label: 'Per device' };
    }
    return { startingAt: Number(s.price) || 0, label: 'Flat' };
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className={`flex-1 ${compareIds.length > 0 ? 'pb-24' : ''}`}>
        <section className="container mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-20">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 text-[10px] uppercase tracking-widest" data-testid="pricing-eyebrow">
              Transparent Pricing
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-4">
              Pricing built for your <span className="text-primary">scale</span>
            </h1>
            <p className="text-base text-muted-foreground mb-6">
              Pick the tier that matches your current size. No long contracts, no hidden fees.
              Every plan can be customised with add-ons; pricing scales with you.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/services')} className="font-bold uppercase" data-testid="see-all-services-btn">
                Browse all services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/login')} className="font-bold uppercase" data-testid="pricing-login-btn">
                Client login
              </Button>
            </div>
          </div>
        </section>

        {loading && (
          <div className="container mx-auto px-4 sm:px-6 md:px-12 py-12 text-center">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground font-mono">Loading plans...</p>
          </div>
        )}

        {!loading && services.length === 0 && (
          <div className="container mx-auto px-4 sm:px-6 md:px-12 py-12 text-center">
            <Card className="p-12 border-border max-w-2xl mx-auto">
              <p className="text-muted-foreground">No services published yet. Check back soon — or call us at <span className="text-primary font-bold">740-302-5607</span>.</p>
            </Card>
          </div>
        )}

        {/* Tiered services — comparison table per service */}
        {grouped.tiered.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 md:px-12 pb-12 sm:pb-20" data-testid="pricing-tiered-section">
            <h2 className="text-2xl sm:text-3xl font-black uppercase mb-6 sm:mb-8">Plan tiers</h2>
            <div className="space-y-10">
              {grouped.tiered.map((service) => {
                const Icon = ICON_MAP[service.icon] || Server;
                const sortedTiers = [...(service.tiers || [])].sort(
                  (a, b) => (a.min_devices || 0) - (b.min_devices || 0)
                );
                const cheapestPrice = sortedTiers.reduce(
                  (lo, t) => Math.min(lo, Number(t.price) || 0),
                  Infinity
                );
                return (
                  <div
                    key={service.service_id}
                    className="rounded-lg border border-border bg-card p-6"
                    data-testid={`pricing-card-${service.service_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-4 mb-6">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight">{service.name}</h3>
                          {service.addons?.length > 0 && (
                            <Badge variant="outline" className="text-[10px] uppercase tracking-widest">
                              + {service.addons.length} add-on{service.addons.length === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{service.description}</p>
                      </div>
                      <div className="md:text-right">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Starting at</p>
                        <p className="text-2xl font-black text-primary font-mono">
                          {fmtUSD(cheapestPrice)}<span className="text-xs text-muted-foreground">/mo</span>
                        </p>
                        <label className="inline-flex items-center gap-2 mt-3 text-xs text-muted-foreground cursor-pointer hover:text-foreground" data-testid={`compare-toggle-${service.service_id}`}>
                          <Checkbox
                            checked={compareIds.includes(service.service_id)}
                            onCheckedChange={() => toggleCompare(service.service_id)}
                            disabled={!compareIds.includes(service.service_id) && compareIds.length >= MAX_COMPARE}
                          />
                          <span>Compare</span>
                        </label>
                      </div>
                    </div>

                    {/* Tier comparison grid — wraps responsively on small screens */}
                    <div className={`grid gap-3 md:grid-cols-${Math.min(sortedTiers.length, 4)}`} style={{ gridTemplateColumns: `repeat(${Math.min(sortedTiers.length, 4)}, minmax(0, 1fr))` }}>
                      {sortedTiers.map((tier, idx) => {
                        const isCheapest = (Number(tier.price) || 0) === cheapestPrice;
                        return (
                          <div
                            key={tier.tier_id}
                            data-testid={`pricing-tier-${service.service_id}-${tier.tier_id}`}
                            className={
                              'rounded-md border p-5 flex flex-col ' +
                              (isCheapest ? 'border-primary/60 bg-primary/5' : 'border-border bg-muted/10')
                            }
                          >
                            <div className="flex items-center justify-between mb-3">
                              <p className="font-bold text-sm uppercase tracking-widest">{tier.name}</p>
                              {isCheapest && (
                                <Badge className="text-[10px] uppercase">Best value</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mb-4">
                              {tier.min_devices}{tier.max_devices ? `–${tier.max_devices}` : '+'} devices
                            </p>
                            <div className="mb-4">
                              <span className="text-3xl font-black text-foreground font-mono">{fmtUSD(tier.price)}</span>
                              <span className="text-xs text-muted-foreground ml-1">/ mo</span>
                            </div>
                            <ul className="space-y-1.5 text-xs text-muted-foreground mb-5 flex-1">
                              {(service.features || []).slice(0, 4).map((f, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <Check className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                                  <span>{f}</span>
                                </li>
                              ))}
                            </ul>
                            <Button
                              variant={isCheapest ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => openDialog(service)}
                              data-testid={`pricing-cta-${service.service_id}-${tier.tier_id}`}
                              className="w-full font-bold uppercase"
                            >
                              {idx === sortedTiers.length - 1 ? 'Talk to sales' : 'Get started'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Per-device services */}
        {grouped.perDevice.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 md:px-12 pb-12 sm:pb-20" data-testid="pricing-perdevice-section">
            <h2 className="text-2xl sm:text-3xl font-black uppercase mb-6 sm:mb-8">Pay per device</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {grouped.perDevice.map((service) => {
                const Icon = ICON_MAP[service.icon] || Server;
                const base = Number(service.price) || 0;
                const per = Number(service.per_device_price) || 0;
                return (
                  <Card key={service.service_id} className="p-5 border-border" data-testid={`pricing-perdevice-${service.service_id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg uppercase tracking-tight mb-1">{service.name}</h3>
                        <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
                        <div className="flex items-baseline gap-3 mb-4">
                          <span className="text-2xl font-black font-mono text-primary">{fmtUSD(per)}</span>
                          <span className="text-xs text-muted-foreground">per device / month</span>
                          {base > 0 && (
                            <span className="text-xs text-muted-foreground">+ {fmtUSD(base)} base</span>
                          )}
                        </div>
                        <Button size="sm" onClick={() => openDialog(service)} data-testid={`pricing-cta-${service.service_id}`} className="font-bold uppercase">
                          Configure & quote
                        </Button>
                        <label className="inline-flex items-center gap-2 ml-3 text-xs text-muted-foreground cursor-pointer hover:text-foreground" data-testid={`compare-toggle-${service.service_id}`}>
                          <Checkbox
                            checked={compareIds.includes(service.service_id)}
                            onCheckedChange={() => toggleCompare(service.service_id)}
                            disabled={!compareIds.includes(service.service_id) && compareIds.length >= MAX_COMPARE}
                          />
                          <span>Compare</span>
                        </label>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Flat-rate services */}
        {grouped.flat.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 md:px-12 pb-12 sm:pb-20" data-testid="pricing-flat-section">
            <h2 className="text-2xl sm:text-3xl font-black uppercase mb-6 sm:mb-8">Flat-rate plans</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped.flat.map((service) => {
                const Icon = ICON_MAP[service.icon] || Server;
                return (
                  <Card key={service.service_id} className="p-5 border-border flex flex-col" data-testid={`pricing-flat-${service.service_id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg uppercase tracking-tight">{service.name}</h3>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3 flex-1">{service.description}</p>
                    <div className="mb-4">
                      <span className="text-3xl font-black font-mono text-primary">{fmtUSD(service.price)}</span>
                      <span className="text-xs text-muted-foreground ml-1">/ {service.billing_cycle}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openDialog(service)} data-testid={`pricing-cta-${service.service_id}`} className="w-full font-bold uppercase">
                      Get a quote
                    </Button>
                    <label className="inline-flex items-center gap-2 mt-3 text-xs text-muted-foreground cursor-pointer hover:text-foreground" data-testid={`compare-toggle-${service.service_id}`}>
                      <Checkbox
                        checked={compareIds.includes(service.service_id)}
                        onCheckedChange={() => toggleCompare(service.service_id)}
                        disabled={!compareIds.includes(service.service_id) && compareIds.length >= MAX_COMPARE}
                      />
                      <span>Add to compare</span>
                    </label>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Trust block / closer */}
        {!loading && services.length > 0 && (
          <section className="container mx-auto px-4 sm:px-6 md:px-12 pb-16 sm:pb-24">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-6 sm:p-10 text-center">
              <h2 className="text-2xl sm:text-3xl font-black uppercase mb-3">Need a custom plan?</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">
                We bundle multi-service discounts for clients with more than 50 devices, hybrid cloud setups,
                or compliance-heavy industries. Tell us about your environment and we'll send a tailored quote within one business day.
              </p>
              <Button onClick={() => navigate('/services')} className="font-bold uppercase" data-testid="custom-plan-cta">
                Talk to an engineer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* Sticky compare bar — appears once at least 1 service is checked. */}
      {compareIds.length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-t border-primary/40 shadow-2xl"
          data-testid="compare-sticky-bar"
        >
          <div className="container mx-auto px-4 sm:px-6 md:px-12 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Scale className="w-5 h-5 text-primary shrink-0" />
              <p className="text-sm">
                <span className="font-bold">{compareIds.length}</span> of {MAX_COMPARE} selected
                <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">
                  Pick up to {MAX_COMPARE} services to compare side-by-side.
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCompareIds([])} data-testid="compare-clear-btn">
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCompare(true)}
                disabled={compareIds.length < 2}
                className="font-bold uppercase"
                data-testid="compare-open-btn"
              >
                Compare {compareIds.length} plan{compareIds.length === 1 ? '' : 's'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <Sheet open={showCompare} onOpenChange={setShowCompare}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto bg-card border-border" data-testid="compare-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-black uppercase tracking-tight">Plan Comparison</SheetTitle>
            <SheetDescription>
              Side-by-side feature and price comparison. Drag column headers to reorder.
            </SheetDescription>
          </SheetHeader>

          {compareServices.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-[10px] uppercase tracking-widest text-muted-foreground w-32">Plan</th>
                    {compareServices.map((s, idx) => (
                      <th
                        key={s.service_id}
                        className={
                          'text-left p-3 min-w-[180px] cursor-move select-none transition-opacity ' +
                          (dragFromIdx === idx ? 'opacity-50' : '')
                        }
                        draggable
                        onDragStart={handleDragStart(idx)}
                        onDragOver={handleDragOver(idx)}
                        onDrop={handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                        data-testid={`compare-col-${s.service_id}`}
                        title="Drag to reorder"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground text-xs select-none" aria-hidden>⋮⋮</span>
                          <p className="font-bold text-base">{s.name}</p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/40">
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted-foreground">Pricing</td>
                    {compareServices.map((s) => {
                      const sum = summarisePricing(s);
                      return (
                        <td key={s.service_id} className="p-3">
                          <Badge variant="outline" className="text-[10px] mb-1">{sum.label}</Badge>
                          <p className="font-mono font-black text-primary text-lg">{fmtUSD(sum.startingAt)}<span className="text-xs text-muted-foreground">/mo</span></p>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-widest">starting at</p>
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted-foreground">Description</td>
                    {compareServices.map((s) => (
                      <td key={s.service_id} className="p-3 text-xs text-muted-foreground">{s.description}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted-foreground align-top">Features</td>
                    {compareServices.map((s) => (
                      <td key={s.service_id} className="p-3 align-top">
                        <ul className="space-y-1.5">
                          {(s.features || []).map((f, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs">
                              <Check className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="p-3 text-[10px] uppercase tracking-widest text-muted-foreground align-top">Add-ons</td>
                    {compareServices.map((s) => (
                      <td key={s.service_id} className="p-3 align-top">
                        {(s.addons || []).length === 0 ? (
                          <span className="text-xs text-muted-foreground italic">None</span>
                        ) : (
                          <ul className="space-y-1">
                            {s.addons.map((a) => (
                              <li key={a.addon_id} className="text-xs">
                                <span className="font-bold">{a.name}</span>
                                <span className="text-muted-foreground ml-2">{fmtUSD(a.price)}{a.charge_type === 'monthly' ? '/mo' : ' once'}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-3"></td>
                    {compareServices.map((s) => (
                      <td key={s.service_id} className="p-3">
                        <Button
                          size="sm"
                          className="w-full font-bold uppercase"
                          onClick={() => { setShowCompare(false); openDialog(s); }}
                          data-testid={`compare-cta-${s.service_id}`}
                        >
                          Get a quote
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ServiceDetailDialog
        service={activeService}
        open={!!activeService}
        onClose={() => setActiveService(null)}
        mode="quote"
      />
    </div>
  );
};

export default Pricing;
